import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';

/**
 * SEO Configuration
 */
const SEO_CONFIG = {
  siteName: 'Mian Tufail Diagnostic Center',
  siteUrl: 'https://mtufaildiagnosticcenter.com',
  defaultTitle: 'Mian Tufail Diagnostic Center - Best MRI & Diagnostic Services in Kasur',
  defaultDescription: 'Mian Tufail Diagnostic Center offers state-of-the-art MRI and diagnostic services in Kasur, Pakistan. Featuring Siemens Magnetom C2 Open MRI technology for accurate diagnosis. Book your appointment today!',
  defaultImage: '/og-image.jpg',
  defaultKeywords: 'MRI Kasur, MRI scan Kasur, diagnostic center Kasur, Siemens MRI Pakistan, Open MRI Pakistan, whole body MRI, MR angiogram, brain MRI, spine MRI, best MRI center Kasur, medical imaging Kasur, Mian Tufail Diagnostic',
  locale: 'en_PK',
  twitterHandle: '@mtufaildiagnostic',
  phone: '+923000478311',
  address: {
    streetAddress: 'Near New Kasur City Housing Society, Opposite New Lari Adda, Main Ferozepur Road',
    addressLocality: 'Kasur',
    addressRegion: 'Punjab',
    postalCode: '55050',
    addressCountry: 'PK',
  },
  geo: {
    latitude: '31.1482307',
    longitude: '74.4545415',
  },
  priceRange: '$$',
  openingHours: [
    'Mo-Sa 08:00-22:00',
    'Su 09:00-18:00',
  ],
};

/**
 * Generate Organization Schema
 */
const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'MedicalBusiness',
  '@id': `${SEO_CONFIG.siteUrl}/#organization`,
  name: SEO_CONFIG.siteName,
  alternateName: 'MTDC',
  url: SEO_CONFIG.siteUrl,
  logo: `${SEO_CONFIG.siteUrl}/logo.png`,
  image: `${SEO_CONFIG.siteUrl}/og-image.jpg`,
  description: SEO_CONFIG.defaultDescription,
  telephone: SEO_CONFIG.phone,
  email: 'info@mtufaildiagnosticcenter.com',
  address: {
    '@type': 'PostalAddress',
    ...SEO_CONFIG.address,
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: SEO_CONFIG.geo.latitude,
    longitude: SEO_CONFIG.geo.longitude,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '08:00',
      closes: '22:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Sunday',
      opens: '09:00',
      closes: '18:00',
    },
  ],
  priceRange: SEO_CONFIG.priceRange,
  sameAs: [
    'https://web.facebook.com/miantufaildiagnosticcenter/',
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'MRI & Diagnostic Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'MedicalTest',
          name: 'Open MRI Scan',
          description: 'Siemens Magnetom C2 Open MRI technology for comfortable, accurate imaging',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'MedicalTest',
          name: 'Whole Body MRI',
          description: 'Comprehensive full body MRI scan for complete health screening',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'MedicalTest',
          name: 'MR Angiogram',
          description: 'Advanced vascular imaging for heart and blood vessel examination',
        },
      },
    ],
  },
  medicalSpecialty: 'http://schema.org/Diagnostic',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '500',
    bestRating: '5',
    worstRating: '1',
  },
});

/**
 * Generate Local Business Schema
 */
const generateLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'DiagnosticLab',
  name: SEO_CONFIG.siteName,
  image: `${SEO_CONFIG.siteUrl}/og-image.jpg`,
  '@id': `${SEO_CONFIG.siteUrl}/#localbusiness`,
  url: SEO_CONFIG.siteUrl,
  telephone: SEO_CONFIG.phone,
  address: {
    '@type': 'PostalAddress',
    ...SEO_CONFIG.address,
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: SEO_CONFIG.geo.latitude,
    longitude: SEO_CONFIG.geo.longitude,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '08:00',
      closes: '22:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Sunday',
      opens: '09:00',
      closes: '18:00',
    },
  ],
  priceRange: SEO_CONFIG.priceRange,
  areaServed: [
    { '@type': 'City', name: 'Kasur' },
    { '@type': 'City', name: 'Lahore' },
    { '@type': 'State', name: 'Punjab' },
  ],
});

/**
 * Generate BreadcrumbList Schema
 */
const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SEO_CONFIG.siteUrl}${item.url}`,
    })),
  };
};

/**
 * Generate WebPage Schema
 */
const generateWebPageSchema = (title, description, url) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SEO_CONFIG.siteUrl}${url}#webpage`,
  url: `${SEO_CONFIG.siteUrl}${url}`,
  name: title,
  description: description,
  isPartOf: {
    '@id': `${SEO_CONFIG.siteUrl}/#website`,
  },
  about: {
    '@id': `${SEO_CONFIG.siteUrl}/#organization`,
  },
  inLanguage: 'en-PK',
});

/**
 * Generate FAQ Schema
 */
const generateFAQSchema = (faqItems) => {
  if (!faqItems || faqItems.length === 0) return null;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.content,
      },
    })),
  };
};

/**
 * Generate Service Schema
 */
const generateServiceSchema = (services) => {
  if (!services || services.length === 0) return null;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: services.map((service, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'MedicalTest',
        name: service.title,
        description: service.description,
        provider: {
          '@id': `${SEO_CONFIG.siteUrl}/#organization`,
        },
      },
    })),
  };
};

/**
 * Enhanced SEO component for managing page meta tags and structured data
 */
const SEO = ({
  title,
  description,
  keywords,
  image,
  url = '/',
  type = 'website',
  noIndex = false,
  noFollow = false,
  breadcrumbs,
  faqItems,
  services,
  article,
}) => {
  const seo = {
    title: title 
      ? `${title} | ${SEO_CONFIG.siteName}` 
      : SEO_CONFIG.defaultTitle,
    description: description || SEO_CONFIG.defaultDescription,
    keywords: keywords || SEO_CONFIG.defaultKeywords,
    image: image ? `${SEO_CONFIG.siteUrl}${image}` : `${SEO_CONFIG.siteUrl}${SEO_CONFIG.defaultImage}`,
    url: `${SEO_CONFIG.siteUrl}${url}`,
  };

  // Robots directive
  const robotsContent = [
    noIndex ? 'noindex' : 'index',
    noFollow ? 'nofollow' : 'follow',
    'max-image-preview:large',
    'max-snippet:-1',
    'max-video-preview:-1',
  ].join(', ');

  // Combine all structured data
  const structuredData = [
    generateOrganizationSchema(),
    generateLocalBusinessSchema(),
    generateWebPageSchema(seo.title, seo.description, url),
    breadcrumbs && generateBreadcrumbSchema(breadcrumbs),
    faqItems && generateFAQSchema(faqItems),
    services && generateServiceSchema(services),
  ].filter(Boolean);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <meta name="author" content={SEO_CONFIG.siteName} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="bingbot" content={robotsContent} />

      {/* Language and Region */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="PK-PB" />
      <meta name="geo.placename" content="Kasur, Punjab, Pakistan" />
      <meta name="geo.position" content={`${SEO_CONFIG.geo.latitude};${SEO_CONFIG.geo.longitude}`} />
      <meta name="ICBM" content={`${SEO_CONFIG.geo.latitude}, ${SEO_CONFIG.geo.longitude}`} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SEO_CONFIG.siteName} - MRI & Diagnostic Services`} />
      <meta property="og:site_name" content={SEO_CONFIG.siteName} />
      <meta property="og:locale" content={SEO_CONFIG.locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SEO_CONFIG.twitterHandle} />
      <meta name="twitter:creator" content={SEO_CONFIG.twitterHandle} />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content={`${SEO_CONFIG.siteName} - MRI & Diagnostic Services`} />

      {/* Article specific tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:author" content={article.author} />
        </>
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={seo.url} />

      {/* Alternate Languages (if applicable) */}
      <link rel="alternate" hrefLang="en" href={seo.url} />
      <link rel="alternate" hrefLang="x-default" href={seo.url} />

      {/* DNS Prefetch for performance */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />

      {/* Preconnect for faster resource loading */}
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

      {/* Structured Data / JSON-LD */}
      {structuredData.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  image: PropTypes.string,
  url: PropTypes.string,
  type: PropTypes.string,
  noIndex: PropTypes.bool,
  noFollow: PropTypes.bool,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ),
  faqItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
    })
  ),
  services: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ),
  article: PropTypes.shape({
    publishedTime: PropTypes.string,
    modifiedTime: PropTypes.string,
    author: PropTypes.string,
  }),
};

export default SEO;
